import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UploadRockImageData } from '../../../core/models/upload-rock-image.model';
import { RemoveExtensionPipe } from '../../pipes/remove-extension.pipe';

/** Maximum accepted file size in gigabytes. */
const MAX_UPLOAD_SIZE_GB = 10;
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_GB * 1024 * 1024 * 1024;

/**
 * Allowed file extensions and their corresponding MIME types.
 * The browser-reported MIME type is a secondary hint; extension is the primary check.
 * Empty string covers cases where the OS does not register a MIME type for the format.
 */
const ALLOWED_TYPES: Record<string, string[]> = {
  '.zip': ['application/zip', 'application/x-zip-compressed', 'application/octet-stream', ''],
  '.ibf': ['application/octet-stream', ''],
};

/** Allow-list for safe filename characters (alphanumeric, space, dash, underscore, dot). */
const SAFE_FILENAME_RE = /^[a-zA-Z0-9 _\-.()\[\]]+$/;

@Component({
  selector: 'app-upload-rock-image-popup',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
    RemoveExtensionPipe
],
  templateUrl: './upload-rock-image-popup.component.html',
  styleUrl: './upload-rock-image-popup.component.scss',
  standalone: true,
})
export class UploadRockImagePopupComponent {
  uploadForm: FormGroup;
  selectedFileName: string | null = null;
  fileError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<UploadRockImagePopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UploadRockImageData,
    private fb: FormBuilder
  ) {
    this.uploadForm = this.fb.group({
      fileFormat: ['raw', Validators.required],
      file: [null, Validators.required],
      dataType: ['uint8', Validators.required],
      resolution: [null, [Validators.required, Validators.min(0.0001)]],
      voxelsX: [null, [Validators.required, Validators.min(1), Validators.pattern('^[0-9]+$')]],
      voxelsY: [null, [Validators.required, Validators.min(1), Validators.pattern('^[0-9]+$')]],
      voxelsZ: [null, [Validators.required, Validators.min(1), Validators.pattern('^[0-9]+$')]],
    });

    this.uploadForm.get('fileFormat')?.valueChanges.subscribe((format) => {
      // Reset any file selection when the format changes — the old file may not match the new format
      this.uploadForm.patchValue({ file: null });
      this.selectedFileName = null;
      this.fileError = null;
      this.onFileFormatChange(format);
    });
  }

  onFileFormatChange(format: 'raw' | 'ibf'): void {
    const dataTypeControl = this.uploadForm.get('dataType');
    const resolutionControl = this.uploadForm.get('resolution');
    const voxelsXControl = this.uploadForm.get('voxelsX');
    const voxelsYControl = this.uploadForm.get('voxelsY');
    const voxelsZControl = this.uploadForm.get('voxelsZ');

    if (format === 'ibf') {
      dataTypeControl?.disable();
      resolutionControl?.disable();
      voxelsXControl?.disable();
      voxelsYControl?.disable();
      voxelsZControl?.disable();
    } else {
      dataTypeControl?.enable();
      resolutionControl?.enable();
      voxelsXControl?.enable();
      voxelsYControl?.enable();
      voxelsZControl?.enable();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    // 1. Validate filename characters
    if (!SAFE_FILENAME_RE.test(file.name)) {
      this.fileError = 'File name contains invalid characters. Use only letters, numbers, spaces, and: _ - . ( ) [ ]';
      this.uploadForm.patchValue({ file: null });
      this.selectedFileName = null;
      input.value = '';
      return;
    }

    // 2. Validate file extension against the selected format
    const selectedFormat: 'raw' | 'ibf' = this.uploadForm.get('fileFormat')?.value;
    const expectedExtension = selectedFormat === 'ibf' ? '.ibf' : '.zip';
    const lowerName = file.name.toLowerCase();

    if (!lowerName.endsWith(expectedExtension)) {
      this.fileError = `Invalid file type. Expected a ${expectedExtension.toUpperCase()} file for the selected format.`;
      this.uploadForm.patchValue({ file: null });
      this.selectedFileName = null;
      input.value = '';
      return;
    }

    // 3. Validate MIME type (secondary check — browser-reported, not trusted alone)
    const allowedMimeTypes = ALLOWED_TYPES[expectedExtension];
    if (file.type && !allowedMimeTypes.includes(file.type)) {
      this.fileError = `Unexpected file content type "${file.type}". Please select a valid ${expectedExtension.toUpperCase()} file.`;
      this.uploadForm.patchValue({ file: null });
      this.selectedFileName = null;
      input.value = '';
      return;
    }

    // 4. Validate file size
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      this.fileError = `File is too large. Maximum allowed size is ${MAX_UPLOAD_SIZE_GB} GB.`;
      this.uploadForm.patchValue({ file: null });
      this.selectedFileName = null;
      input.value = '';
      return;
    }

    // All checks passed
    this.fileError = null;
    this.uploadForm.patchValue({ file });
    this.selectedFileName = file.name;
  }

  onConfirm(): void {
    if (this.uploadForm.valid && !this.fileError) {
      const formValue = this.uploadForm.getRawValue();
      this.dialogRef.close(formValue);
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  isIbfFormat(): boolean {
    return this.uploadForm.get('fileFormat')?.value === 'ibf';
  }
}
